import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Tag } from 'antd';
import { EditOutlined, UndoOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { GoodsProps } from '../api/Goods';
import styled from 'styled-components';

const StyledRow = styled(Row)`
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 8px 0;
`;

const StyledCol = styled(Col)`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  border-right: 1px solid #e0e0e0;
  min-width: 0;
  &:last-child {
    border-right: none;
  }
`;

const Truncate = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  max-height: 52px;
  overflow: hidden;
`;

const EditIcon = styled(EditOutlined)`
  cursor: pointer;
  color: #1890ff;
  font-size: 18px;
  &:hover {
    color: #40a9ff;
  }
`;

const RestoreIcon = styled(UndoOutlined)`
  cursor: pointer;
  color: #52c41a;
  font-size: 18px;
  &:hover {
    color: #73d13d;
  }
`;

const DeleteIcon = styled(DeleteOutlined)`
  cursor: pointer;
  color: #ff4d4f;
  font-size: 18px;
  margin-left: 12px;
  &:hover {
    color: #ff7875;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const mpShort = (marketplace: string) => {
  const m = marketplace.toLowerCase();
  if (m === 'wildberries' || m === 'wb') return 'WB';
  if (m === 'ozon') return 'Ozon';
  return marketplace;
};

export const GoodsItemView = ({
  data,
  onEdit,
  onDelete,
  onRestore,
}: {
  data: GoodsProps;
  onEdit: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
}) => {
  const isDeleted = !!data.deleted_at;
  const tags = data.tags || [];
  const mpLinks = data.mp_links || [];

  return (
    <StyledRow>
      <StyledCol md={1}>
        <Truncate title={data.article}>
          <Link
            to={`/settings/goods/${encodeURIComponent(data.article)}`}
            style={{ fontWeight: 600 }}
          >
            {data.article}
          </Link>
        </Truncate>
      </StyledCol>
      <StyledCol md={2}>
        <Truncate title={data.name}>{data.name}</Truncate>
      </StyledCol>
      <StyledCol md={3}>
        {mpLinks.length ? (
          <TagsWrap
            title={mpLinks
              .map((l) => {
                const label = mpShort(l.marketplace);
                if (!l.linked) return `${label}: нет`;
                return `${label}: ${l.is_archived ? 'архив' : 'есть'}`;
              })
              .join(', ')}
          >
            {mpLinks.map((l) => (
              <Tag
                key={l.marketplace_auth_id}
                color={
                  !l.linked ? 'default' : l.is_archived ? 'orange' : 'green'
                }
                style={{ margin: 0 }}
              >
                {mpShort(l.marketplace)}
                {mpLinks.filter((x) => x.marketplace === l.marketplace).length >
                1
                  ? ` #${l.marketplace_auth_id}`
                  : ''}
              </Tag>
            ))}
          </TagsWrap>
        ) : (
          <Tag color="warning" style={{ margin: 0 }}>
            нет МП
          </Tag>
        )}
      </StyledCol>
      <StyledCol md={1}>
        {data.product_remnants?.show_for_distribution ? '✓' : '-'}
      </StyledCol>
      <StyledCol md={1}>{data.is_combo ? '✓' : '-'}</StyledCol>
      <StyledCol md={1}>
        <Truncate title={data.brand?.name || undefined}>
          {data.brand?.name || '-'}
        </Truncate>
      </StyledCol>
      <StyledCol md={1}>
        <Truncate title={data.category?.name || undefined}>
          {data.category?.name || 'без категории'}
        </Truncate>
      </StyledCol>
      <StyledCol md={1}>
        {tags.length ? (
          <TagsWrap title={tags.map((t) => t.name).join(', ')}>
            {tags.map((t) => (
              <Tag key={t.id ?? t.name} style={{ margin: 0 }}>
                {t.name}
              </Tag>
            ))}
          </TagsWrap>
        ) : (
          '—'
        )}
      </StyledCol>
      <StyledCol md={1} style={{ justifyContent: 'center' }}>
        <Actions>
          {isDeleted && onRestore ? (
            <RestoreIcon title="Восстановить" onClick={onRestore} />
          ) : (
            <>
              <Link
                to={`/settings/goods/${encodeURIComponent(data.article)}`}
                title="Открыть карточку"
              >
                <EditIcon />
              </Link>
              {onDelete && (
                <DeleteIcon title="Удалить" onClick={onDelete} />
              )}
            </>
          )}
        </Actions>
      </StyledCol>
    </StyledRow>
  );
};
